import { FormLeavingListener } from '@exam-management/components';
import {
  GetQuestionCreationTaskByIdDocument,
  GetTaskQuestionGroupByIdDocument,
  QuestionGroupType,
  useCreateInDevelopmentQuestionMutation,
} from '@exam-management/graphql';
import { type QBankStructureLevel } from '@exam-management/qbs-provider';
import { useQuestionCreationTaskDetails } from '@exam-management/question-creation-task-details';
import {
  useGetQBSLevelAndLevelItemsForTask,
  useItemSetQuestionDetails,
} from '@exam-management/question-creation-task-question-management';
import { parseContentForApi } from '@exam-management/rich-text-editor';
import { useNavigate } from '@exam-management/router';
import { zodResolver } from '@hookform/resolvers/zod';
import { Form } from '@shared/components';
import { useSnackbar } from '@shared/snackbar';
import { type PropsWithChildren } from '@shared/utils';
import { useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { matchPath, useLocation } from 'react-router-dom';

import { parseQuestionFormDataForApi } from '../single-question-creation-form';

import { DEFAULT_FORM_VALUES } from './constants';
import {
  type CreateItemSetSubQuestionFormInput,
  getCreateItemSetSubQuestionSchema,
} from './item-set-sub-question-creation.schema';
import { getLocationDefaultValue } from './utils';

interface ItemSetSubQuestionCreationFormProps extends PropsWithChildren {
  qbsLevels: QBankStructureLevel[];
}

export default function ItemSetSubQuestionCreationForm(
  props: Readonly<ItemSetSubQuestionCreationFormProps>,
) {
  const { children, qbsLevels } = props;

  const navigate = useNavigate();
  const location = useLocation();

  const { task } = useQuestionCreationTaskDetails();
  const { itemSet } = useItemSetQuestionDetails();

  const matchedTaskManagement = matchPath('/task-management/*', location.pathname);
  const createItemSetSubQuestionSchema = useMemo(
    () => getCreateItemSetSubQuestionSchema(qbsLevels),
    [qbsLevels],
  );
  const { questionStructures, id: groupId } = itemSet;
  const qBankStructureId = task.metadata?.requirement.qBankStructure?.id ?? '';
  const { qbsLevelItems } = useGetQBSLevelAndLevelItemsForTask(qBankStructureId, task.id);

  const defaultLocationValues = useMemo(
    () => getLocationDefaultValue(questionStructures, qbsLevelItems),
    [questionStructures, qbsLevelItems],
  );
  const formContext = useForm<CreateItemSetSubQuestionFormInput>({
    defaultValues: {
      ...DEFAULT_FORM_VALUES,
      location: { ...defaultLocationValues },
    },
    resolver: zodResolver(createItemSetSubQuestionSchema),
  });

  const { reset } = formContext;

  useEffect(() => {
    reset({
      ...DEFAULT_FORM_VALUES,
      location: { ...defaultLocationValues },
    });
  }, [defaultLocationValues, reset]);

  const { showSnackbar } = useSnackbar();

  const [createInDevelopmentQuestion] = useCreateInDevelopmentQuestionMutation({
    onError: () => {
      showSnackbar({
        message: 'Unable to Save Question',
        variant: 'error',
      });
    },
  });

  const handleShowRequiredFieldsError = () => {
    showSnackbar({
      message: 'Please complete all required fields',
      variant: 'error',
    });
  };
  const handleItemSetSubQuestionCreationFormSubmit = async (
    formData: CreateItemSetSubQuestionFormInput,
  ) => {
    const qBankStructureLevelItemId = formData.location.SUB_TOPIC?.value as string;
    const qBankStructureId = task.metadata?.requirement.qBankStructure?.id;

    if (!qBankStructureLevelItemId || !qBankStructureId)
      throw Error('Invalid qbs id and level item id');

    const payload = parseQuestionFormDataForApi(
      formData,
      parseContentForApi,
      QuestionGroupType.ItemSet,
      groupId,
    );

    const result = await createInDevelopmentQuestion({
      variables: {
        input: {
          qBankStructureLevelItemId,
          refQBankStructureId: qBankStructureId,
          data: { ...payload },
        },
        taskId: task.id,
      },
      awaitRefetchQueries: true,
      refetchQueries: [
        {
          query: GetQuestionCreationTaskByIdDocument,
          variables: {
            getTaskByIdInput: {
              taskId: task.id,
            },
          },
        },
        {
          query: GetTaskQuestionGroupByIdDocument,
          variables: {
            questionGroupId: groupId,
            taskId: task.id,
          },
        },
      ],

      onCompleted: (data) => {
        showSnackbar({
          message: 'Question has been saved',
          variant: 'success',
        });
        const subQuestionPath = matchedTaskManagement
          ? '/task-management/question-creation/:taskId/item-set/:questionId/:subQuestionId'
          : '/my-task/question-creation/:taskId/item-set/:questionId/:subQuestionId';

        navigate(subQuestionPath, {
          params: {
            taskId: task.id,
            subQuestionId: data?.qctCreateInDevelopmentQuestion.id,
            questionId: itemSet?.id,
          },
        });
      },
    });

    if (result.errors) throw result.errors;
  };

  return (
    <FormProvider {...formContext}>
      <Form
        onSubmit={formContext.handleSubmit(
          handleItemSetSubQuestionCreationFormSubmit,
          handleShowRequiredFieldsError,
        )}
      >
        {children}
      </Form>
      <FormLeavingListener onSubmit={handleItemSetSubQuestionCreationFormSubmit} />
    </FormProvider>
  );
}
